import React, { useEffect, useState } from "react";
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import dayjs from "dayjs";
import { useForm } from "antd/es/form/Form";
import { useNavigate, Link } from "react-router-dom";
import { Col, Form, Input, Row, Select, Button, Upload, message, DatePicker, Flex, Spin } from "antd";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { CSVLink } from "react-csv";
import { Mapper } from "./Mapper";
import { Preview } from "./Preview";
import { SectionHeader } from "../../components/SectionHeader/SectionHeader";
import { reportMappings } from "../../constants/reportMappings";
import { Partners } from "./Partners";
import classNames from "classnames";
import css from "./Report.module.css"; 

export const UploadForm = ({submitUploadForm}) => {
  const [form] = useForm();
  const [sheetPicker, setSheetPicker] = useState(null); // { fileName, fileContents, sheets }

  
const readFileAsync = (file) => {
  return new Promise((resolve, reject) => {
	const reader = new FileReader();
	reader.onload = (event) => {
	  resolve(event.target.result); // ArrayBuffer now, not text
	};
	reader.onerror = (error) => {
	  reject(error);
	};
	reader.readAsArrayBuffer(file); // <-- was readAsText
  });
};

  // 0-based column index -> Excel-style letter (0 -> A, 9 -> J, 26 -> AA, ...)
  const excelColumnLetter = (index) => {
	let letter = '';
	let num = index + 1;
	while (num > 0) {
		const rem = (num - 1) % 26;
		letter = String.fromCharCode(65 + rem) + letter;
		num = Math.floor((num - 1) / 26);
	}
	return letter;
  };

  const readCsv = (csv) => {
	return new Promise((resolve, reject) => {
		Papa.parse(csv, {
		header: true,
		skipEmptyLines: true,
		// no explicit delimiter — let Papa auto-detect
		transformHeader: (header, index) => {
			const trimmed = (header || '').trim();
			// blank headers all collapse onto the same "" key and overwrite each
			// other — name them by column position instead so nothing is lost.
			return trimmed ? trimmed : `column_${excelColumnLetter(index)}`;
		},
		transform: (value) => {
			value = value.trim();
			if (value.startsWith("'") && !value.endsWith("'")) {
			value = value.substring(1);
			}
			return value;
		},
		complete: (results) => resolve(results),
		error: (error) => reject(error),
		});
	});
  }

  const getSheets = (xls) => {
		const workbook = XLSX.read(xls, { type: 'array', bookSheets: true });
		return workbook.SheetNames;
  }

  const cleanUp = str => {
	const linesArray = str.split('\n');
	const commaRegex = /,{6,}/;

	const filteredLines = linesArray.filter(line => {
		return !commaRegex.test(line);
	});

	return filteredLines.join('\n');
  }

  // shared finisher: turns a chosen sheet (or the only sheet, for CSV a pre-decoded string)
  // into normalised rows and pushes it into the form field.
  const finalizeCsv = async (processedCSV, fileName) => {
	if (processedCSV) {
		const CSVData = await readCsv(processedCSV);
		form.setFieldValue('file', {name: fileName, content: CSVData.data, headers: CSVData.meta.fields} );
		form.validateFields(['file']);
	} else {
		form.setFields([{ name: 'file', errors: ['Could not parse file'] }]);
	}
  };

  // converts one sheet of a raw XLS ArrayBuffer into cleaned CSV and finalizes it.
  // parses only the requested sheet (not the whole workbook) — matters for large files.
  const processSheet = async (fileContents, sheetName, fileName) => {
	const workbook = XLSX.read(fileContents, { type: 'array', raw: false, cellText: true, sheets: [sheetName] });
	const sheet = workbook.Sheets[sheetName];
	let processedCSV = XLSX.utils.sheet_to_csv(sheet); // defaults to comma delimiter
	processedCSV = cleanUp(processedCSV);
	await finalizeCsv(processedCSV, fileName);
  };

  const onSheetSelected = async (sheetName) => {
	if (!sheetPicker) return;
	await processSheet(sheetPicker.fileContents, sheetName, sheetPicker.fileName);
	setSheetPicker(null);
  };

  const beforeUpload = async (file) => {
	const fileName = file.name; 
	const isCsv = file.type === 'text/csv' || file.type === 'application/csv';
	const isXls = file.type === 'application/vnd.ms-excel' || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
	if (!(isCsv || isXls)) {
	  message.error('You can only upload CSV or XLS files!');
	  return Upload.LIST_IGNORE;
	}

	// reset any pending sheet picker from a previous file
	setSheetPicker(null);

	let fileContents = await readFileAsync(file);

	if(isXls) {
		// cheap metadata-only read: gets sheet names without parsing cell data
		const sheets = getSheets(fileContents);

		if (sheets.length > 1) {
			// pause the automatic flow — wait for the user to pick a sheet.
			// clear the field for now so validation/submit can't proceed with stale data.
			form.setFieldValue('file', undefined);
			setSheetPicker({ fileName, fileContents, sheets });
			return Upload.LIST_IGNORE;
		}

		await processSheet(fileContents, sheets[0], fileName);
		return Upload.LIST_IGNORE;
	}

	if(isCsv) {
		const processedCSV = new TextDecoder('utf-8').decode(fileContents);
		await finalizeCsv(processedCSV, fileName);
	}

	return Upload.LIST_IGNORE;
  };
  
  const rules = [
	{
	  required: true,
	  message: "This field is required",
	},
  ];

  const renderSampleDownload = () => {
	const sampleData = reportMappings.map(field => ({[field.value]:''}));
	return <CSVLink data={sampleData} filename="sample.csv">Download Sample</CSVLink>
  }

  return (
	<>
		<Form
		form={form}
		name="importReportForm"
		autoComplete="off"
		layout="vertical"
		initialValues={{report_date: dayjs()}}
		onFinish={submitUploadForm}
		>
			<Flex className={css.root} vertical>
				<div className={"ibox-holder"}>
					<SectionHeader title="Choose CSV or XLS file" />
					<Row gutter={16}>
						<Col xs={24} md={18} lg={12} xl={8} xxl={6}>
						<Form.Item
							name="partner"
							className={classNames(css.reportFormItem, css.inlineFormItem)}
							label={<span className={css.label}>Partner</span>}
							rules={rules}
						>
							<Partners />
						</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={24} md={18} lg={12} xl={8} xxl={6}>
						<Form.Item
							name="report_date"
							className={classNames(css.reportFormItem, css.inlineFormItem)}
							label={<span className={css.label}>Report Date</span>}
							rules={rules}
							defaultValue={dayjs()}
							getValueProps={(i) => ({ value: dayjs(i) })}
						>
							<DatePicker
								format='YYYY-MM-DD'
								placeholder='YYYY-MM-DD'
								allowClear={false}
							/>
						</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={24} md={8}>
						<Form.Item
							name="file"
							className={classNames(css.reportFormItem, css.inlineFormItem, css.uploadCsv)}
							
							rules={rules}
						>
						
						<Upload
							name="csv"
							showUploadList={false}
							beforeUpload={beforeUpload}
							
							>
							<Button>Choose CSV/XLS File</Button>
							{renderSampleDownload()}
						</Upload>
						
						</Form.Item>
						</Col>
					</Row>
					{sheetPicker && (
						<Row gutter={16}>
							<Col xs={24} md={8}>
								<Form.Item
									className={classNames(css.reportFormItem, css.inlineFormItem)}
									label={<span className={css.label}>Select Sheet</span>}
									validateStatus="warning"
									help={`"${sheetPicker.fileName}" has multiple sheets — choose one to import`}
								>
									<Select
										placeholder="Choose a sheet"
										onChange={onSheetSelected}
										options={sheetPicker.sheets.map((s) => ({ label: s, value: s }))}
									/>
								</Form.Item>
							</Col>
						</Row>
					)}
					<Row gutter={16}>
						<Col xs={24} md={8}>
							<Form.Item
								className={css.buttonsHolder}
							>
								<Button
									size="large"
									rootClassName={css.submitButton}
									htmlType="submit"
									loading={false}
									type="primary"
								>
									Next
								</Button> 
							</Form.Item>
						</Col>
					</Row>
				</div>
			</Flex>
		</Form>
	</>
  );
  
};